import type {
  CardioState,
  CoreState,
  PushUpsState,
  ScoreResult,
  WhtrState,
} from '@/features/scoring/lib/types'
import { isFieldEntered } from '@/features/scoring/lib/scoringInput'
import type { UFPMScoreEntry } from './types'

export interface ScoreHistoryMeta {
  id: number | string
  recordedDate: string
  memberId: string
  memberName: string
}

function cardioEntered(cardio: CardioState): boolean {
  if (cardio.exempt) return false
  if (cardio.type === 'run') return isFieldEntered(cardio.runMin) || isFieldEntered(cardio.runSec)
  return isFieldEntered(cardio.shuttles)
}

function coreEntered(core: CoreState): boolean {
  if (core.exempt) return false
  if (core.type === 'plank') return isFieldEntered(core.plankMin) || isFieldEntered(core.plankSec)
  return isFieldEntered(core.reps)
}

export function componentWasCompleted(
  entry: UFPMScoreEntry,
  component: 'pu' | 'core' | 'cardio' | 'whtr',
): boolean {
  const explicit = {
    pu: entry.puCompleted,
    core: entry.coreCompleted,
    cardio: entry.cardioCompleted,
    whtr: entry.whtrCompleted,
  }[component]
  if (explicit !== undefined) return explicit

  const exempt = {
    pu: entry.puEx,
    core: entry.coreEx,
    cardio: entry.cardioEx,
    whtr: entry.whtrEx,
  }[component]
  if (exempt) return false

  const score = {
    pu: entry.puScore,
    core: entry.coreScore,
    cardio: entry.cardioScore,
    whtr: entry.whtrScore,
  }[component]
  return score != null && String(score).trim() !== ''
}

export function buildScoreHistoryEntry(
  scored: ScoreResult,
  meta: ScoreHistoryMeta,
  inputs: { pushUps: PushUpsState; core: CoreState; cardio: CardioState; whtr: WhtrState },
): UFPMScoreEntry {
  return {
    ...scored,
    ...meta,
    puCompleted: !inputs.pushUps.exempt && isFieldEntered(inputs.pushUps.reps),
    coreCompleted: coreEntered(inputs.core),
    cardioCompleted: cardioEntered(inputs.cardio),
    whtrCompleted: !inputs.whtr.exempt && isFieldEntered(inputs.whtr.waist) && isFieldEntered(inputs.whtr.height),
  }
}